/**
 * LavanderiasTab — CRUD del catálogo de lavanderías (`/lavanderia` → pestaña
 * "Lavanderías").
 *
 * Aporta visibilidad operativa pensando en el admin que mantiene el catálogo:
 *
 *  - Cada fila muestra el conteo de propiedades, lavados y gastos asociados,
 *    con badges para detectar de un vistazo qué lavanderías están "vivas".
 *  - Las filas son expandibles: al abrirlas carga lazy la lista de propiedades
 *    asociadas (endpoint `getLavanderiaPropiedades`). Útil tanto para inspección
 *    rápida como para entender qué se va a mover si se elimina la lavandería.
 *  - La fila "Lavandería externa" (es_generica=true) está marcada con un Tag
 *    "Sistema" y bloqueada para edición/eliminación. Sigue mostrando sus
 *    propiedades asociadas: ahora toda propiedad que no tenga AF interna
 *    asignada apunta a esta fila por default (regla del proyecto).
 *  - El borrado de una lavandería con dependencias abre `DeleteLavanderiaModal`
 *    (selector de destino + confirmación con resumen de impacto). El backend
 *    reasigna en transacción: propiedades, lavados (tabla `lavados`) y
 *    gastos operacionales (`lavanderia_af_gastos`). Para gastos en colisión
 *    por período, los del origen se descartan (decisión explícita del owner).
 *
 * Si la lavandería NO tiene dependencias, se mantiene el flujo simple
 * (Popconfirm) sin abrir el modal de reasignación.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import DeleteLavanderiaModal from "./DeleteLavanderiaModal";
import { paginacionCliente } from "../../utils/tablaPaginacion";

const { Text } = Typography;

/** Fila tal como la devuelve el backend (extiende con counts agregados). */
type LavanderiaRow = {
  id: number;
  nombre: string;
  direccion?: string;
  descripcion?: string;
  activo: boolean;
  es_generica?: boolean;
  propiedades_count?: number;
  lavados_count?: number;
  gastos_count?: number;
  reembolsos_count?: number;
};

/** Propiedad listada en la fila expandida. */
type PropiedadAsociada = {
  id: number;
  nombrepropiedad: string;
  habilitado: boolean;
  id_cliente: number | null;
  cliente: string | null;
};

/**
 * Props del componente.
 *
 * `onCatalogChanged` lo dispara el componente cada vez que el catálogo de
 * lavanderías cambia (alta, edición, toggle activo, eliminación). El
 * componente padre (`lavanderia.tsx`) lo conecta con `refreshLavanderias`
 * de `useLavanderiaLogic` para que el dropdown del tab "Registros" se
 * mantenga sincronizado: antes había un bug donde una lavandería borrada
 * desde acá seguía apareciendo en el filtro hasta hacer un refresh manual.
 */
interface LavanderiasTabProps {
  onCatalogChanged?: () => void;
}

export default function LavanderiasTab({ onCatalogChanged }: LavanderiasTabProps = {}) {
  const [items, setItems] = useState<LavanderiaRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LavanderiaRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Caché en memoria de las propiedades por lavandería: lo poblamos al
  // expandir cada fila (lazy) para evitar N llamadas al cargar la tabla.
  // La invalidamos cuando se elimina una lavandería (las propiedades pueden
  // haberse reasignado al destino y aparecer ahora ahí).
  const [propsByLav, setPropsByLav] = useState<Record<number, PropiedadAsociada[]>>({});
  const [propsLoading, setPropsLoading] = useState<Record<number, boolean>>({});
  const [expandedKeys, setExpandedKeys] = useState<readonly number[]>([]);

  // Estado del modal de eliminación con reasignación (solo aparece si la
  // lavandería tiene dependencias).
  const [deleteTarget, setDeleteTarget] = useState<LavanderiaRow | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const res = await finanzasEmpresaApi.getLavanderias();
    if (res?.data) setItems(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const fetchPropiedades = useCallback(async (lavanderiaId: number) => {
    setPropsLoading((prev) => ({ ...prev, [lavanderiaId]: true }));
    const res = await finanzasEmpresaApi.getLavanderiaPropiedades(lavanderiaId);
    setPropsByLav((prev) => ({ ...prev, [lavanderiaId]: res?.data ?? [] }));
    setPropsLoading((prev) => ({ ...prev, [lavanderiaId]: false }));
  }, []);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await finanzasEmpresaApi.updateLavanderia(editing.id, values);
      } else {
        await finanzasEmpresaApi.createLavanderia(values);
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      fetchAll();
      // Aviso al tab "Registros" para que su dropdown se actualice con la
      // alta o el rename. Sin esto, el otro tab seguía mostrando el nombre
      // viejo / sin la nueva entrada hasta hacer refresh manual.
      onCatalogChanged?.();
    } finally {
      setSaving(false);
    }
  };

  // Borrado simple: solo se llega acá cuando NO hay dependencias. Si las
  // hubiera, el botón abre `DeleteLavanderiaModal`.
  const handleDeleteSimple = async (id: number) => {
    const res = await finanzasEmpresaApi.deleteLavanderia(id);
    // apiFetch con DELETE 204 devuelve null en éxito, pero también null en
    // error (mostró toast). Si la fila desaparece tras refetch, asumimos OK.
    void res;
    fetchAll();
    setPropsByLav({});
    onCatalogChanged?.();
  };

  // Cuando el usuario completa el modal de reasignación con éxito.
  const handleDeleteWithReassignSuccess = () => {
    setDeleteTarget(null);
    setExpandedKeys([]);
    setPropsByLav({});
    fetchAll();
    onCatalogChanged?.();
  };

  const handleToggleActivo = async (record: LavanderiaRow) => {
    await finanzasEmpresaApi.updateLavanderia(record.id, { activo: !record.activo });
    fetchAll();
    // Toggle activo afecta qué lavanderías aparecen como destino válido en el
    // modal de reasignación (filtramos por `activo`), por eso también avisa.
    onCatalogChanged?.();
  };

  const onExpand = (expanded: boolean, record: LavanderiaRow) => {
    setExpandedKeys((prev) => {
      if (expanded) return [...prev, record.id];
      return prev.filter((k) => k !== record.id);
    });
    // Lazy-load: solo pegamos al backend si aún no tenemos las propiedades
    // de esta lavandería en caché. Si el admin cierra y vuelve a abrir, no
    // se vuelve a cargar (las invalidaciones se hacen tras delete/refresh).
    if (expanded && !propsByLav[record.id] && !propsLoading[record.id]) {
      fetchPropiedades(record.id);
    }
  };

  // Renderiza el contenido expandido: lista de propiedades asociadas o
  // empty state si la lavandería no tiene ninguna.
  const renderExpanded = useCallback(
    (record: LavanderiaRow) => {
      const isLoading = propsLoading[record.id];
      const propiedades = propsByLav[record.id];

      if (isLoading) {
        return (
          <div style={{ padding: 16, textAlign: "center" }}>
            <Spin size="small" /> <Text type="secondary">Cargando propiedades…</Text>
          </div>
        );
      }
      if (!propiedades || propiedades.length === 0) {
        return (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                Sin propiedades asociadas a esta lavandería.
              </Text>
            }
            style={{ margin: "12px 0" }}
          />
        );
      }
      return (
        <div style={{ padding: "8px 12px" }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            <HomeOutlined /> {propiedades.length} propiedad{propiedades.length !== 1 ? "es" : ""}
            {" asociada"}
            {propiedades.length !== 1 ? "s" : ""}
          </Text>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 8,
            }}
          >
            {propiedades.map((p) => (
              <div
                key={p.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: p.habilitado ? "#fafafa" : "#fff7e6",
                }}
              >
                <div style={{ fontWeight: 500 }}>
                  {formatPropertyName(p.nombrepropiedad)}
                  {!p.habilitado && (
                    <Tag color="warning" style={{ marginLeft: 6 }}>
                      deshabilitada
                    </Tag>
                  )}
                </div>
                {p.cliente && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {p.cliente}
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    },
    [propsByLav, propsLoading],
  );

  // Otras lavanderías (excluyendo la actual y las inactivas) que pueden ser
  // destino de reasignación al eliminar. Lo calculamos acá porque el listado
  // ya está en memoria — no hace falta volver a pedirlo.
  const destinosForDelete = useMemo(
    () =>
      items.filter(
        (l) => deleteTarget != null && l.id !== deleteTarget.id && l.activo,
      ),
    [items, deleteTarget],
  );

  const columns = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      sorter: (a: LavanderiaRow, b: LavanderiaRow) => a.nombre.localeCompare(b.nombre),
      render: (v: string, record: LavanderiaRow) => (
        <Space size={6}>
          <span>{v}</span>
          {record.es_generica && (
            <Tooltip title="Lavandería del sistema. No se puede renombrar ni eliminar. Es la opción por defecto cuando una propiedad no tiene una lavandería AF interna asignada.">
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                Sistema
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: (
        <Space size={4}>
          Propiedades
          <Tooltip title="Cantidad de propiedades cuyo lavanderia_id apunta a esta fila. Click en el '+' de cada fila para ver la lista.">
            <InfoCircleOutlined style={{ color: "#999" }} />
          </Tooltip>
        </Space>
      ),
      key: "propiedades",
      width: 130,
      align: "center" as const,
      render: (_: any, record: LavanderiaRow) => {
        const count = record.propiedades_count ?? 0;
        return (
          <Badge
            count={count}
            showZero
            color={count > 0 ? "#1677ff" : "#d9d9d9"}
            overflowCount={9999}
          />
        );
      },
    },
    {
      title: (
        <Space size={4}>
          Lavados
          <Tooltip title="Registros de lavado (tabla `lavados`) asociados.">
            <InfoCircleOutlined style={{ color: "#999" }} />
          </Tooltip>
        </Space>
      ),
      key: "lavados",
      width: 100,
      align: "center" as const,
      render: (_: any, record: LavanderiaRow) => {
        const count = record.lavados_count ?? 0;
        return (
          <Badge
            count={count}
            showZero
            color={count > 0 ? "#52c41a" : "#d9d9d9"}
            overflowCount={9999}
          />
        );
      },
    },
    {
      title: (
        <Space size={4}>
          Gastos op.
          <Tooltip title="Registros de gastos operacionales (lavanderia_af_gastos): un registro por mes por lavandería.">
            <InfoCircleOutlined style={{ color: "#999" }} />
          </Tooltip>
        </Space>
      ),
      key: "gastos",
      width: 100,
      align: "center" as const,
      render: (_: any, record: LavanderiaRow) => {
        const count = record.gastos_count ?? 0;
        return (
          <Badge
            count={count}
            showZero
            color={count > 0 ? "#fa8c16" : "#d9d9d9"}
            overflowCount={9999}
          />
        );
      },
    },
    {
      title: "Dirección",
      dataIndex: "direccion",
      key: "direccion",
      ellipsis: true,
      render: (v: string) => v || "-",
    },
    {
      title: "Activa",
      dataIndex: "activo",
      key: "activo",
      width: 80,
      align: "center" as const,
      render: (v: boolean, record: LavanderiaRow) => (
        <Switch size="small" checked={v} onChange={() => handleToggleActivo(record)} />
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 120,
      align: "center" as const,
      render: (_: any, record: LavanderiaRow) => {
        const totalDeps =
          (record.propiedades_count ?? 0) +
          (record.lavados_count ?? 0) +
          (record.gastos_count ?? 0) +
          (record.reembolsos_count ?? 0);
        const hasDeps = totalDeps > 0;
        const isGenerica = !!record.es_generica;

        return (
          <Space size="small">
            <Tooltip title={isGenerica ? "La lavandería del sistema no se puede renombrar" : "Editar"}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={isGenerica}
                onClick={() => {
                  setEditing(record);
                  form.setFieldsValue({
                    nombre: record.nombre,
                    direccion: record.direccion,
                    descripcion: record.descripcion,
                  });
                  setModalOpen(true);
                }}
              />
            </Tooltip>
            {/* Si hay deps: abrimos el modal de reasignación. Si no, Popconfirm clásico. */}
            {isGenerica ? (
              <Tooltip title="La lavandería del sistema no se puede eliminar">
                <Button size="small" danger icon={<DeleteOutlined />} disabled />
              </Tooltip>
            ) : hasDeps ? (
              <Tooltip title={`Eliminar (tiene ${totalDeps} registro${totalDeps !== 1 ? "s" : ""} asociado${totalDeps !== 1 ? "s" : ""})`}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setDeleteTarget(record)}
                />
              </Tooltip>
            ) : (
              <Popconfirm
                title="¿Eliminar lavandería?"
                description="No tiene propiedades, lavados ni gastos asociados."
                okText="Eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDeleteSimple(record.id)}
              >
                <Tooltip title="Eliminar">
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "16px 0" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div>
          <strong style={{ fontSize: 16 }}>Lavanderías registradas</strong>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
            Click en el "+" de cada fila para ver las propiedades asociadas.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing(null);
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Nueva lavandería
        </Button>
      </div>

      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={paginacionCliente({ porDefecto: 10, etiqueta: "lavandería", size: "small" })}
        scroll={{ x: "max-content" }}
        expandable={{
          expandedRowRender: renderExpanded,
          expandedRowKeys: expandedKeys as number[],
          onExpand,
        }}
      />

      <Modal
        open={modalOpen}
        title={editing ? "Editar lavandería" : "Nueva lavandería"}
        onOk={handleSave}
        confirmLoading={saving}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: "Requerido" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="direccion" label="Dirección">
            <Input />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {deleteTarget && (
        <DeleteLavanderiaModal
          target={deleteTarget}
          destinos={destinosForDelete}
          // Pasamos las propiedades cacheadas para que el modal las muestre
          // directamente. Si no están cacheadas, el modal las pide solo.
          propiedadesCached={propsByLav[deleteTarget.id]}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleteWithReassignSuccess}
        />
      )}
    </div>
  );
}
